import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Facebook, Instagram, Youtube, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getPublicFooter } from "../../api/footer";
import { resolveAssetUrl } from "../../utils/resolveAssetUrl";
import apiClient from "../../api/axios";

/**
 * SocialSidebar component that stays sticky on the left side of the screen.
 * Fetches links from the public footer/site settings API.
 */
export const SocialSidebar: React.FC = () => {
    const { data: footer, isLoading } = useQuery({
        queryKey: ["publicFooter"],
        queryFn: getPublicFooter,
    });

    const { data: homeConfig } = useQuery({
        queryKey: ['publicHomePage'],
        queryFn: async () => {
            const response = await apiClient.get('/public/homepage');
            return response.data.data;
        }
    });

    if (isLoading || !footer) return null;

    const dict = homeConfig?.dictionary;
    const nLinks = [];
    if (dict?.t_NewsletterButton1Url && dict.t_NewsletterButton1Url !== "#") {
        nLinks.push({ label: dict.t_NewsletterButton1Text || "Newsletter 1", url: resolveAssetUrl(dict.t_NewsletterButton1Url) });
    }
    if (dict?.t_NewsletterButton2Url && dict.t_NewsletterButton2Url !== "#") {
        nLinks.push({ label: dict.t_NewsletterButton2Text || "Newsletter 2", url: resolveAssetUrl(dict.t_NewsletterButton2Url) });
    }

    const socialLinks = [
        { 
            icon: <Facebook className="h-5 w-5" />, 
            url: footer.facebookUrl, 
            label: "Facebook",
            color: "#1877F2",
            contrastColor: "#ffffff"
        },
        { 
            icon: <Instagram className="h-5 w-5" />, 
            url: footer.instagramUrl, 
            label: "Instagram",
            color: "#E4405F",
            contrastColor: "#ffffff"
        },
        { 
            icon: (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            ), 
            url: footer.twitterUrl, 
            label: "X",
            color: "#000000",
            contrastColor: "#ffffff"
        },
        { 
            icon: <Youtube className="h-5 w-5" />, 
            url: footer.youtubeUrl, 
            label: "YouTube",
            color: "#FF0000",
            contrastColor: "#ffffff"
        },
        ...(nLinks.length > 0 ? [{ 
            icon: <Mail className="h-5 w-5" />, 
            label: dict?.t_NewsletterTitle || "Newsletter",
            color: "#F9B934",
            contrastColor: "#000000",
            subLinks: nLinks
        }] : []),
    ].filter(link => link.url || (link.subLinks && link.subLinks.length > 0));

    if (socialLinks.length === 0) return null;

    return (
        <div className="fixed right-0 top-1/2 z-50 -translate-y-1/2 hidden lg:flex flex-col gap-2 items-end">
            <AnimatePresence>
                {socialLinks.map((social, index) => {
                    const Wrapper = social.subLinks ? motion.div : motion.a;
                    const props = social.subLinks ? {} : { href: social.url, target: "_blank", rel: "noopener noreferrer" };

                    return (
                        <Wrapper
                            key={social.label}
                            {...props as any}
                            initial={{ x: 40, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: index * 0.1 + 0.5, duration: 0.5, type: "spring" }}
                            whileHover={{ x: -5 }}
                            className="group relative flex items-center flex-row-reverse cursor-pointer"
                            style={{ "--hover-color": social.color, "--contrast-color": social.contrastColor } as any}
                        >
                            {/* Background with Glassmorphism */}
                            <div className="absolute inset-0 -z-10 rounded-l-xl bg-background/80 backdrop-blur-md border border-r-0 border-border shadow-lg transition-colors group-hover:bg-primary/10" />
                            
                            {/* Icon Container */}
                            <div 
                                className="flex h-12 w-12 items-center justify-center transition-colors text-[var(--hover-color)] group-hover:text-[var(--contrast-color)]"
                            >
                                <div className="relative z-10">
                                    {social.icon}
                                </div>
                                
                                {/* Animated background fill on hover */}
                                <div 
                                    className="absolute inset-0 rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                    style={{ backgroundColor: "var(--hover-color)" }}
                                />
                            </div>

                            {/* Label / SubLinks (Slides out on hover) */}
                            <div className={`relative z-10 flex ${social.subLinks ? 'flex-col justify-center gap-1.5 group-hover:py-3' : 'items-center'} max-w-0 overflow-hidden whitespace-nowrap px-0 text-sm font-bold transition-all duration-300 group-hover:max-w-[400px] group-hover:px-4`}>
                                {social.subLinks ? (
                                    <>
                                        <span className="text-black/70 text-xs uppercase tracking-wider pl-1">{social.label}:</span>
                                        <div className="flex flex-col gap-1.5">
                                            {social.subLinks.map(sub => (
                                                <a 
                                                    key={sub.label} 
                                                    href={sub.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="transition-colors text-black hover:text-white px-2.5 py-1.5 rounded-md border border-black/20 bg-white/70 hover:bg-black shadow-sm text-[13px]"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {sub.label}
                                                </a>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <span className="text-foreground group-hover:text-[var(--contrast-color)] transition-colors drop-shadow-sm">{social.label}</span>
                                )}
                            </div>
                        </Wrapper>
                    );
                })}
            </AnimatePresence>
        </div>
    );
};
